import {
    eventRepository,
    gameRepository,
    seasonRepository
} from "../storage/storage";
import GameAccess from "../sports/common/game-access";
import EventStatus from "../sports/common/event-status";
import GamedayApiService from "./gameday-api-service";
import SportCodes, { getSportDisplayName } from "../sports/common/sport-codes";
import FootballService from "./football-service";
import { getAllowedSportCodes } from "./common/config-util";

class SecurityService {
    constructor() {
        this.gameAccessBySport = {
            [SportCodes.Football]: (eventId) =>
                this.getFootballEventAccess(eventId),
            [SportCodes.FlagFootball]: (eventId) =>
                this.getFootballEventAccess(eventId),
            [SportCodes.Tennis]: (eventId) =>
                this.getTennisEventAccess(eventId),
            [SportCodes.WomenTennis]: (eventId) =>
                this.getTennisEventAccess(eventId)
        };
    }

    async getSportAccess() {
        const permittedSportCodes = getAllowedSportCodes();
        const optionArr = [];
        Object.entries(SportCodes).forEach((sportCode) => {
            if (permittedSportCodes.some((code) => code === sportCode[1]))
                optionArr.push({
                    name: getSportDisplayName(sportCode[1]),
                    value: sportCode[1]
                });
        });
        return optionArr;
    }

    async _isGameDataComplete(eventId) {
        let result = true;
        const event = await eventRepository.getById(eventId);
        const { sportId } = event;

        if (
            SportCodes.FlagFootball === sportId ||
            SportCodes.Football === sportId
        ) {
            const game = await gameRepository
                .where({ eventId: eventId })
                .first();
            if (game) {
                const { isComplete } = await FootballService.isGameDataComplete(
                    game.id
                );
                result = isComplete;
            } else {
                result = false;
            }
        }

        return result;
    }

    async getTennisEventAccess() {
        const gameAccess = new Set();
        gameAccess.add(GameAccess.GAME_CONFIG);
        gameAccess.add(GameAccess.HOME_TEAM_INFO);
        gameAccess.add(GameAccess.AWAY_TEAM_INFO);
        gameAccess.add(GameAccess.EVENT_INFO);
        gameAccess.add(GameAccess.ADD_PLAYS);
        gameAccess.add(GameAccess.FINALIZE);
        gameAccess.add(GameAccess.VIEW_STATS);
        gameAccess.add(GameAccess.STREAM);

        return Array.from(gameAccess);
    }

    async getFootballEventAccess(eventId) {
        const gameAccess = new Set();
        const event = await eventRepository.getById(eventId);
        if (event) {
            gameAccess.add(GameAccess.ADD_PLAYS);

            if (!EventStatus.isFinal(event)) {
                gameAccess.add(GameAccess.GAME_CONFIG);
                gameAccess.add(GameAccess.HOME_TEAM_INFO);
                gameAccess.add(GameAccess.AWAY_TEAM_INFO);
                gameAccess.add(GameAccess.EVENT_INFO);
                gameAccess.add(GameAccess.STREAM);
            }

            const isGameDataComplete = await this._isGameDataComplete(eventId);
            if (!EventStatus.isFinal(event) && isGameDataComplete) {
                gameAccess.add(GameAccess.FINALIZE);
            }

            if (EventStatus.isFinal(event) || isGameDataComplete) {
                gameAccess.add(GameAccess.VIEW_STATS);
            }

            if (isGameDataComplete) {
                const { seasonId } = event;
                const season = await seasonRepository
                    .where({ seasonId })
                    .first();
                if (season) {
                    let events = { data: [] };
                    if (!EventStatus.isStarted(event)) {
                        events = await GamedayApiService.getUpcomingEvents(
                            season.id,
                            {
                                localSearch: true
                            }
                        );
                    } else if (EventStatus.isInProgress(event)) {
                        events = await GamedayApiService.getInProgressEvents(
                            season.id,
                            {
                                localSearch: true
                            }
                        );
                    }

                    if (events.data.find((ue) => ue.id === event.id)) {
                        gameAccess.add(GameAccess.ADD_PLAYS);
                    }
                }
            }
        }

        return Array.from(gameAccess);
    }

    async getEventAccess(eventId) {
        const { sportId } = await eventRepository.getById(eventId);
        return this.gameAccessBySport[sportId]
            ? this.gameAccessBySport[sportId](eventId)
            : [];
    }

    async getGameAccess(gameId) {
        const game = await gameRepository.getById(gameId);
        if (game) {
            return this.getEventAccess(game.eventId);
        }

        return [];
    }
}

export default new SecurityService();
